import type { SupportingPagesUnifiedColumn } from './supporting-pages-unified-columns';
import type { DomainTopicUrlRow } from './topic-supporting-pages';

/**
 * The Supporting pages card, reduced to what a review decision needs.
 *
 * The deep-dive card answers several questions at once: which domains rank for
 * this URL's topics, what their strongest page is, what you already cover, and
 * what you don't. That is right for Inventory, where an operator is working out
 * what is going on.
 *
 * On the review surface the card answers exactly one question — **what closely
 * related pages do they have that I don't?** — because the only thing being
 * decided is whether to queue writing them. Everything that is not that answer
 * is furniture there, and each piece of furniture is a thing the operator has
 * to read past before they can press a key.
 *
 * So three things are dropped, and each was asked for by name:
 *
 * **Your own site's column.** It answers a different question — *do I have a
 * supporting cluster?* — which is worth asking and is not this card's question.
 *
 * **Their top page.** It is the domain's strongest page on the topic, which
 * establishes why the domain is worth listening to. Useful when you are
 * orienting; noise when you already are.
 *
 * **Rows you already cover.** If the card is a list of pages to write, a page
 * you have written is not on it.
 *
 * What is deliberately NOT dropped is any gap. "Show everything" applies: every
 * gap row survives, because each one is a line rather than a panel.
 */

export interface ReviewSupportingPagesGap {
  url: string;
  /** Pathname only — the domain is stated once, on the column. */
  path: string;
  /** Formatted by the host, which owns the existing traffic wording. */
  traffic: string | null;
}

export interface ReviewSupportingPagesColumn {
  domain: string;
  /** "productive.io has 2 pages on this topic you don't." */
  verdict: string;
  gaps: ReviewSupportingPagesGap[];
}

export interface ReviewSupportingPagesFormatters {
  path: (url: string) => string;
  traffic: (value?: number) => string | null;
}

/**
 * A competitor row is a gap when nothing on your site covers it.
 *
 * The same `similarOnYourSite` the deep-dive card paints green or red, read
 * here as a filter rather than a colour. That it is the same field is the point:
 * two surfaces disagreeing about which pages you are missing would be worse
 * than either of them being sparse.
 */
function isGap(row: DomainTopicUrlRow): boolean {
  return !row.similarOnYourSite;
}

function verdictFor(domain: string, count: number): string {
  return count === 1
    ? `${domain} has 1 page on this topic you don’t.`
    : `${domain} has ${count} pages on this topic you don’t.`;
}

export function reviewSupportingPagesColumns(
  columns: readonly SupportingPagesUnifiedColumn[] | null | undefined,
  format: ReviewSupportingPagesFormatters
): ReviewSupportingPagesColumn[] {
  const out: ReviewSupportingPagesColumn[] = [];

  for (const col of columns ?? []) {
    if (!col || col.isYourSite) continue;

    const gaps = (col.supportingRows ?? []).filter(isGap).map((row) => ({
      url: row.url,
      path: format.path(row.url),
      traffic: format.traffic(row.estimatedOrganicTraffic)
    }));

    // A column with nothing missing has nothing to say on this surface. On the
    // deep-dive card it still earns its place — it is evidence that you cover
    // the topic — but here it would be a heading over an empty list.
    if (!gaps.length) continue;

    out.push({ domain: col.domain, verdict: verdictFor(col.domain, gaps.length), gaps });
  }

  return out;
}

/** Whether the review card has anything to say at all. */
export function hasReviewSupportingPagesGaps(
  columns: readonly ReviewSupportingPagesColumn[] | null | undefined
): boolean {
  return (columns ?? []).length > 0;
}
